import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, tokenStore, apiErr } from "@/lib/api";
import { connectRealtime, disconnectRealtime } from "@/lib/realtime";
import { hydrateRegistry, resetRegistry } from "@/lib/clientRegistry";
import { hydrateLeads, resetLeads } from "@/lib/leadsData";
import { hydrateLeadRouter, resetLeadRouter } from "@/lib/leadRouter";
import { hydrateCampaigns, resetCampaigns } from "@/lib/campaignData";
import { resetOps } from "@/lib/tasksData";
import { hydrateNotes, resetNotes } from "@/lib/notesData";
import { resetReports } from "@/lib/reportStore";
import { hydrateCalendar, resetCalendar } from "@/lib/calendarData";
import { hydrateAdministration, resetAdministration } from "@/lib/administrationData";
import { hydrateAccounting, resetAccounting } from "@/lib/accountingData";
import { hydrateCrm, resetCrm } from "@/lib/crmData";
import { hydrateSupport, resetSupport } from "@/lib/supportTicketsData";
import { hydrateAlerts, resetAlerts } from "@/lib/alertsData";
import { hydrateComm, resetComm } from "@/lib/commData";
import { hydrateTeam, resetTeam } from "@/lib/teamData";
import { hydrateHr, hydrateMyHr, resetHr } from "@/lib/hrData";

const ADMIN_PERMS = ["view_administration", "create_proposals", "view_contracts", "manage_settings"];

// Real auth (Phase 1): JWT via FastAPI /api/auth. Ruoli interni (admin/manager/
// specialist/...) + client_user per il portale. L'oggetto `user` mantiene la stessa
// forma del mock precedente (id, name, email, role, initials, avatar, clientId).
const AuthContext = createContext(null);

// Tutti i dati iniziali in PARALLELO (prima erano in sequenza: tempo = somma di ~15 chiamate).
async function hydrateAll(user) {
  const perms = user?.permissions || [];
  const internal = user?.role !== "client_user";
  const jobs = [hydrateLeads()];
  if (internal) jobs.push(hydrateTeam(), hydrateNotes(), hydrateAlerts(user?.id), hydrateComm(perms), perms.includes("view_hr") ? hydrateHr() : hydrateMyHr());
  if (perms.includes("view_clients")) jobs.push(hydrateRegistry());
  if (perms.includes("view_lead_router")) jobs.push(hydrateLeadRouter(perms));
  if (perms.includes("view_campaigns")) jobs.push(hydrateCampaigns());
  if (perms.includes("view_calendar")) jobs.push(hydrateCalendar());
  if (perms.some((p) => ADMIN_PERMS.includes(p))) jobs.push(hydrateAdministration());
  if (perms.includes("view_accounting")) jobs.push(hydrateAccounting());
  if (perms.includes("view_crm")) jobs.push(hydrateCrm());
  if (perms.includes("view_support")) jobs.push(hydrateSupport());
  await Promise.allSettled(jobs);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const boot = async () => {
      if (!tokenStore.get()) { setLoading(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        if (active) setUser(data.user);
        await hydrateAll(data.user);
        connectRealtime();
      } catch {
        tokenStore.clear();
      } finally {
        if (active) setLoading(false);
      }
    };
    boot();
    return () => { active = false; };
  }, []);

  const afterLogin = useCallback(async (data) => {
    tokenStore.set(data.access_token, data.refresh_token);
    setUser(data.user);
    await hydrateAll(data.user);
    connectRealtime();
    return { ok: true, user: data.user };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.otp_required) {
        return { ok: false, otpRequired: true, pendingToken: data.pending_token, emailHint: data.email_hint, resendIn: data.resend_in };
      }
      return await afterLogin(data);
    } catch (e) {
      return { ok: false, error: apiErr(e, "Email o password non corretti.") };
    }
  }, [afterLogin]);

  const verifyOtp = useCallback(async (pendingToken, code) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { pending_token: pendingToken, code });
      return await afterLogin(data);
    } catch (e) {
      return { ok: false, error: apiErr(e, "Codice non valido.") };
    }
  }, [afterLogin]);

  const finalizeGoogle = useCallback(async (code, state) => {
    try {
      const { data } = await api.post("/auth/google/callback", { code, state });
      return await afterLogin(data);
    } catch (e) {
      return { ok: false, error: apiErr(e, "Accesso con Google non riuscito.") };
    }
  }, [afterLogin]);

  const recoveryLogin = useCallback(async (email, password, recoveryCode) => {
    try {
      const { data } = await api.post("/auth/login/recovery", { email, password, recovery_code: recoveryCode });
      return await afterLogin(data);
    } catch (e) {
      return { ok: false, error: apiErr(e, "Credenziali o codice di recupero non validi.") };
    }
  }, [afterLogin]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    disconnectRealtime();
    tokenStore.clear();
    resetRegistry();
    resetLeads();
    resetLeadRouter();
    resetCampaigns();
    resetOps();
    resetNotes();
    resetReports();
    resetCalendar();
    resetAdministration();
    resetAccounting();
    resetCrm();
    resetSupport();
    resetAlerts();
    resetComm();
    resetTeam();
    resetHr();
    setUser(null);
  }, []);

  const isClient = user?.role === "client_user";

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyOtp, finalizeGoogle, recoveryLogin, logout, isAuthenticated: !!user, isClient }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
